import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";

import {
  initialState,
  handleFulfilled,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper,
  handlePayload
} from "helpers/reducerHelpers";

const shipmentPlanUrl = "/crm/shipment/plans";

export const getShipmentPlans = createAsyncThunk(
  "Shipment/getShipmentPlans",
  async (params) => {
    try {
      const response = await apiAxios.get(shipmentPlanUrl, { params } )
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const createShipmentPlan = createAsyncThunk(
  "Shipment/createShipmentPlan",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(shipmentPlanUrl, data)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const updateShipmentPlan = createAsyncThunk(
  "Shipment/updateShipmentPlan",
  async ({ shipmentId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${shipmentPlanUrl}/${shipmentId}`, data)
      return response.data
    }
    catch(error) {
      return rejectWithValue(error.response.data);
    }
  }
)

// The API refuses (422) once the plan has been sent to Logistic.
export const deleteShipmentPlan = createAsyncThunk(
  "Shipment/deleteShipmentPlan",
  async (shipmentId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${shipmentPlanUrl}/${shipmentId}`);
      return { shipmentId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message })
    }
  }
)

export const exportData = createAsyncThunk(
  "Shipment/exportData",
  async (params) => {
    //remove page and per_page in object
    const { page, per_page, ...rest } = params
    console.log(rest, page, per_page)

    const response = await apiAxios.get(`${shipmentPlanUrl}/export`, {params, responseType: 'blob'})
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `shipment-plan.xlsx`);
    document.body.appendChild(link);
    link.click();
  }
)

export const confirmToLogistic = createAsyncThunk(
  "Shipment/confirmToLogistic",
  async (formData) => {
    try {
      const response = await apiAxios.post(`${shipmentPlanUrl}/confirm`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data;
    }
    catch(error) {
      throw new Error(error.response?.data?.error || error.message)
    }
  }
)

// Admin-only undo of "Send to Logistic" — the API refuses it once the Logistic
// department has released the booking.
export const rollbackShipmentPlan = createAsyncThunk(
  "Shipment/rollbackShipmentPlan",
  async (shipmentId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${shipmentPlanUrl}/${shipmentId}/rollback`)
      return response.data;
    }
    catch(error) {
      return rejectWithValue(error.response?.data || { error: error.message })
    }
  }
)

// Admin "Adjust weights": every shipment row of the plan's building — BOM, what
// is released already, what this plan holds, and a suggestion for a plan that
// carries its weight only on itself (Logistic import / hand-made / pre-release).
export const getPlanAllocations = createAsyncThunk(
  "Shipment/getPlanAllocations",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${shipmentPlanUrl}/${planId}/allocations`)
      return response.data?.data || []
    }
    catch(error) {
      return rejectWithValue(error.response?.data || { error: error.message })
    }
  }
)

// Admin-only, in any stage. `allocations` = [{ shipment_id, released_weight, bom_weight? }];
// only the rows sent change, and a weight of 0 takes a row out of the plan.
export const allocateShipmentPlan = createAsyncThunk(
  "Shipment/allocateShipmentPlan",
  async ({ planId, allocations }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${shipmentPlanUrl}/${planId}/allocations`, { allocations })
      return response.data
    }
    catch(error) {
      return rejectWithValue(error.response?.data || { error: error.message })
    }
  }
)

export const getShipmentJobsByPhases = createAsyncThunk(
  "Shipment/getShipmentJobsByPhases",
  async (params) => {
    try {
      const response = await apiAxios.get(`${shipmentPlanUrl}/list_jobs_has_phases`, { params })
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const listProjectShopPhases = createAsyncThunk(
  "Shipment/listProjectShopPhases",
  async (building_id) => {
    try {
      const response = await apiAxios.get(`${shipmentPlanUrl}/load_shop_phases`, { params: { building_id } })
      return response.data;
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getShipmentMailer = createAsyncThunk(
  "Shipment/getShipmentMailer",
  // plan_ids pick the Local (Vietnam) or Oversea recipient list.
  async (planIds = []) => {
    try {
      const response = await apiAxios.get(`${shipmentPlanUrl}/mailer`, { params: { plan_ids: planIds } })
      return response.data;
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)


const isRejectedAction = (action) => (action.type.startsWith("Shipment/")) && action.type.endsWith("/rejected");

const shipmentPlanSlice = createSlice({
  name: "crm_shipment_plan",
  initialState: {
    ...initialState,
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getShipmentPlans.fulfilled, (state, action) => handleFulfilled(state, action, "allData"))
      
      .addCase(createShipmentPlan.fulfilled, (state, action) => {
        handlePayload(state, action, "create")
      })
      .addCase(updateShipmentPlan.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(deleteShipmentPlan.fulfilled, (state, action) => {
        const { shipmentId, data } = action.payload;

        // The project-detail tab dispatches this too, where allData was never loaded.
        if (Array.isArray(state.allData?.data)) {
          state.allData.data = state.allData.data.filter((item) => item.id !== shipmentId)
        }
        toast.success(data?.message || 'Shipment plan deleted.');
      })
      .addCase(rollbackShipmentPlan.fulfilled, (state, action) => {
        // `data` is the JSON:API object for the rolled-back plan — replace the
        // row so confirmed_date/confirmed_by and the permission flags refresh.
        const { data, message } = action.payload;

        // The project-detail tab dispatches this too, where allData was never
        // loaded — guard instead of assuming the list is present.
        if (data?.id && Array.isArray(state.allData?.data)) {
          state.allData.data = state.allData.data.map((item) =>
            item.id === data.id ? { id: data.id, ...data.attributes } : item
          );
        }
        toast.success(message);
      })
      .addCase(exportData.pending, (state) => {
        state.exporting = true
      })
      .addCase(exportData.fulfilled, (state) => {
        state.exporting = false
        toast.success("Data has been exported successfully.")
      })
      .addCase(confirmToLogistic.fulfilled, (state, action) => {
        const { data, message, error } = action.payload;

        if (error !== undefined) {
          toast.error(error);
        } else {
          // `data` is a JSON:API array ({ id, attributes }) — flatten it like the
          // rollback reducer so confirmed_date and the permission flags refresh.
          // The project-detail tab sends too, where allData was never loaded.
          if (Array.isArray(data) && Array.isArray(state.allData?.data)) {
            const updated = new Map(data.map((row) => [row.id, { id: row.id, ...row.attributes }]));
            state.allData.data = state.allData.data.map((item) =>
              updated.has(item.id) ? { ...item, ...updated.get(item.id) } : item
            );
          }
          toast.success(message);
        }
      })
      .addMatcher(
        isRejectedAction,
        (state, action) => {
          state.loading = false;
          toast.error(action.payload?.message || action.payload?.error || action?.error?.message)
        }
      );
  }
})

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = shipmentPlanSlice.actions

export default shipmentPlanSlice.reducer